import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import type { CancelPaymentBody, PausePaymentBody, Payment } from '@klopotun/contracts';
import { cancelPayment } from '../requests/cancel-payment.js';
import { pausePayment } from '../requests/pause-payment.js';
import { resumePayment } from '../requests/resume-payment.js';
import { invalidatePayment } from './payment-cache.js';

export type PaymentStatusInput =
  | { action: 'pause'; id: string; body: PausePaymentBody }
  | { action: 'resume'; id: string }
  | { action: 'cancel'; id: string; body: CancelPaymentBody };

const changePaymentStatus = (input: PaymentStatusInput): Promise<Payment> => {
  if (input.action === 'pause') {
    return pausePayment(input.id, input.body);
  }
  if (input.action === 'cancel') {
    return cancelPayment(input.id, input.body);
  }
  return resumePayment(input.id);
};

export const usePaymentStatusMutation = (): UseMutationResult<
  Payment,
  Error,
  PaymentStatusInput
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: changePaymentStatus,
    onSuccess: (payment) => invalidatePayment(queryClient, payment.id),
  });
};
